import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Camera, EdgeCamera } from '../../../../cameras/camera.model';
import { LocationModel } from '../../../../locations/location.model';
import { CameraAuthState } from '../wizard-authentication/wizard-authentication.component';
import { ConfigureCamerasDialogComponent } from '../../dialogs/configure-cameras-dialog/configure-cameras-dialog.component';
import { cameraTypeStr } from '@models/camera.model';
import { ThumbnailsService } from 'src/app/development/thumbnails.service';
import { StorageBucket } from '@consts/storage-url.util';
import { api } from '@consts/url.const';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

// Stream settings interface for optimization checks
export interface StreamSetting {
    pixelsCount: number;
    fps: number;
    bitrateKbps: number;
    videoQuality: number;
    compression: number;
    iframeFactor: number;
}

// Mainstream settings by resolution
export const SettingsMainstream: StreamSetting[] = [
    { pixelsCount: 230400, fps: 15, bitrateKbps: 600, videoQuality: 60, compression: 40, iframeFactor: 1 },    //  640 *  360
    { pixelsCount: 307200, fps: 15, bitrateKbps: 800, videoQuality: 60, compression: 40, iframeFactor: 1 },    //  640 *  480
    { pixelsCount: 921600, fps: 15, bitrateKbps: 1800, videoQuality: 60, compression: 40, iframeFactor: 1 },   // 1280 *  720
    { pixelsCount: 2073600, fps: 15, bitrateKbps: 2048, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 1920 * 1080
    { pixelsCount: 3145728, fps: 15, bitrateKbps: 2560, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 3072 * 1028
    { pixelsCount: 3686400, fps: 15, bitrateKbps: 3584, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 2560 * 1440
    { pixelsCount: 4665600, fps: 15, bitrateKbps: 3584, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 2880 * 1620
    { pixelsCount: 5038848, fps: 15, bitrateKbps: 3584, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 2592 * 1944
    { pixelsCount: 7516800, fps: 15, bitrateKbps: 5120, videoQuality: 60, compression: 40, iframeFactor: 1 },  // 3480 * 2160
    { pixelsCount: 12000000, fps: 15, bitrateKbps: 6144, videoQuality: 60, compression: 40, iframeFactor: 1 }, // 4000 * 3000
];

// Substream settings by resolution
export const SettingsSubstream: StreamSetting[] = [
    { pixelsCount: 230400, fps: 25, bitrateKbps: 700, videoQuality: 60, compression: 40, iframeFactor: 2 },   //  640 *  360
    { pixelsCount: 307200, fps: 25, bitrateKbps: 700, videoQuality: 60, compression: 40, iframeFactor: 2 },   //  640 *  480
    { pixelsCount: 921600, fps: 25, bitrateKbps: 700, videoQuality: 60, compression: 40, iframeFactor: 2 },   // 1280 *  720
    { pixelsCount: 2073600, fps: 25, bitrateKbps: 1024, videoQuality: 60, compression: 40, iframeFactor: 2 }, // 1920 * 1080
];

// Stream detail row for the optimization table
export interface StreamDetailRow {
    label: string;
    current: string | number;
    setTo?: string | number;  // Only shown if not optimized
}

// Camera configuration state for the configuration stage
export interface CameraConfigState {
    // Camera identity (from auth stage)
    id: string;
    name: string;  // editable

    // Original camera from discovery (has cameraBrand, macAddress, onvifData)
    originalCamera: Camera;

    // Connection details (from auth response) - contains cameraDocument, mainStreamConnectionData, subStreamConnectionData
    connectionDetails: LocationModel.CameraConnectionDetails;

    // User-configurable settings (with defaults)
    autoConfigure: boolean;  // default: false (true for Lumana)
    cameraType: EdgeCamera.CameraType;  // default: StaticCamera

    // Storage settings (defaults)
    storageMaxDays: number;  // default: 30
    storageFromSubstream: boolean;  // default: true (if substream available)
    smartStorageEnabled: boolean;  // default: true (if substream available)

    // Audio settings
    audioMainStreamEnabled: boolean;
    audioSubStreamEnabled: boolean;

    // Audio (if available - for future use)
    hasAudio: boolean;
}

@Component({
    selector: 'app-wizard-configuration',
    templateUrl: './wizard-configuration.component.html',
    styleUrls: ['./wizard-configuration.component.scss'],
    standalone: false,
})
export class WizardConfigurationComponent implements OnInit, OnDestroy {
    @Input() selectedCameras: Camera[] = [];
    @Input() cameraAuthStates: Map<string, CameraAuthState> = new Map();
    @Input() edgeId: string;
    @Input() locationId: string;

    @Output() finishSetup = new EventEmitter<CameraConfigState[]>();
    @Output() selectedConfigsChanged = new EventEmitter<Set<string>>();
    @Output() back = new EventEmitter<void>();

    // Camera configurations
    public cameraConfigs: Map<string, CameraConfigState> = new Map();

    // Selected cameras for bulk configuration
    public selectedCameraIds: Set<string> = new Set();

    // Camera type options
    public CameraType = EdgeCamera.CameraType;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private translateService: TranslateService,
        private thumbnailsService: ThumbnailsService,
        private changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.thumbnailsService.privateStorageMapReady$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                setTimeout(() => this.changeDetectorRef.detectChanges());
            });
        this.initializeCameraConfigs();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeCameraConfigs(): void {
        this.cameraConfigs.clear();
        this.selectedCameraIds.clear();

        this.cameraAuthStates.forEach((authState, id) => {
            const isGenetec = !!authState.camera?.genetecGuid;
            const connectionDetails = authState.connectionDetails
                ?? (isGenetec ? this.buildGenetecConnectionDetails(id) : null);

            if (connectionDetails) {
                const subConnectionResponseCode = connectionDetails.subStreamConnectionData?.connectionResponseCode;
                const hasSubstream = subConnectionResponseCode >= 200 && subConnectionResponseCode < 300;

                const config: CameraConfigState = {
                    id: id,
                    name: authState.camera.name || this.translateService.instant('COMMON.CAMERA'),
                    originalCamera: authState.camera,
                    connectionDetails: connectionDetails,
                    autoConfigure: true,
                    cameraType: EdgeCamera.CameraType.StaticCamera,
                    storageMaxDays: 30,
                    storageFromSubstream: hasSubstream,
                    smartStorageEnabled: hasSubstream,
                    audioMainStreamEnabled: false,
                    audioSubStreamEnabled: false,
                    hasAudio: true, // Future: detect from stream data
                };

                this.cameraConfigs.set(id, config);
            }
        });
    }

    private buildGenetecConnectionDetails(id: string): LocationModel.CameraConnectionDetails {
        const emptyStream: LocationModel.StreamConnectionData = {
            connectionResponseCode: 200,
            snapshotUrl: '',
        };
        return {
            id,
            mainStreamConnectionData: emptyStream,
            subStreamConnectionData: { connectionResponseCode: 0 },
        };
    }

    // Get configs as array for template iteration
    public get cameraConfigsArray(): CameraConfigState[] {
        return Array.from(this.cameraConfigs.values());
    }

    // Selection methods
    public isCameraSelected(cameraId: string): boolean {
        return this.selectedCameraIds.has(cameraId);
    }

    public toggleCameraSelection(cameraId: string): void {
        if (this.selectedCameraIds.has(cameraId)) {
            this.selectedCameraIds.delete(cameraId);
        } else {
            this.selectedCameraIds.add(cameraId);
        }
        this.selectedConfigsChanged.emit(this.selectedCameraIds);
    }

    public toggleAllCameras(): void {
        const allConfigs = this.cameraConfigsArray;
        const allSelected = allConfigs.every(c => this.selectedCameraIds.has(c.id));

        if (allSelected) {
            this.selectedCameraIds.clear();
        } else {
            allConfigs.forEach(c => this.selectedCameraIds.add(c.id));
        }
        this.selectedConfigsChanged.emit(this.selectedCameraIds);
    }

    public get areAllSelected(): boolean {
        const allConfigs = this.cameraConfigsArray;
        return allConfigs.length > 0 && allConfigs.every(c => this.selectedCameraIds.has(c.id));
    }

    public get selectedCount(): number {
        return this.selectedCameraIds.size;
    }

    // Stream status helpers
    public getMainstreamStatusIcon(config: CameraConfigState): string {
        const code = config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode;
        return this.getStatusIconClass(code);
    }

    public getSubstreamStatusIcon(config: CameraConfigState): string {
        const code = config.connectionDetails?.subStreamConnectionData?.connectionResponseCode;
        if (!code) return 'ui-icon-minus'; // No substream
        return this.getStatusIconClass(code);
    }

    private getStatusIconClass(code: number | undefined): string {
        if (code >= 200 && code < 300) {
            return 'ui-icon-status-online status-success';
        } else if (code === 401 || code === 404) {
            return 'ui-icon-warning status-warning';
        } else if (code) {
            return 'ui-icon-error status-error';
        }
        return 'ui-icon-minus status-none';
    }

    private normalizeSnapshotPath(snapshotPath: string): string {
        if (!snapshotPath.startsWith('http')) {
            return snapshotPath.replace(/^\/+/, '');
        }

        const markers = [
            '/storage-download/snapshots/',
            '/lumanaai-snapshots/',
            `${api.thumbnails.gcpSnapshotsUrl}/`,
            `${api.thumbnails.awsSnapshotsUrl}/`,
        ];
        for (const marker of markers) {
            const idx = snapshotPath.indexOf(marker);
            if (idx !== -1) {
                return snapshotPath.slice(idx + marker.length).replace(/^\/+/, '');
            }
        }
        return snapshotPath;
    }

    // Prefer private storage for this edge/location when configured.
    public getSnapshotUrl(snapshotPath: string | undefined): string {
        if (!snapshotPath) return '';
        const relativePath = this.normalizeSnapshotPath(snapshotPath);
        if (relativePath.startsWith('http')) {
            return relativePath;
        }
        return `${this.thumbnailsService.getBucketBaseUrl(
            StorageBucket.Snapshots,
            true,
            false,
            this.edgeId,
            this.locationId,
        )}/${relativePath}`;
    }

    /** Cloud/proxy fallback when the private MinIO URL fails to load. */
    public getSnapshotFallbackUrl(snapshotPath: string | undefined): string {
        if (!snapshotPath) return '';
        const relativePath = this.normalizeSnapshotPath(snapshotPath);
        if (relativePath.startsWith('http')) {
            return relativePath;
        }
        return `${this.thumbnailsService.getBucketBaseUrl(StorageBucket.Snapshots)}/${relativePath}`;
    }

    // Storage display helpers
    public getStorageStreamText(config: CameraConfigState): string {
        return config.storageFromSubstream ? 'CAMERA_ACTIVATE_DIALOG.SUB_STREAM' : 'CAMERA_ACTIVATE_DIALOG.MAIN_STREAM';
    }

    public getStorageDaysText(config: CameraConfigState): string {
        return this.translateService.instant('ADD_CAMERA_WIZARD.CONFIGURATION.STORAGE_DAYS', { count: config.storageMaxDays });
    }

    // Camera type display
    public getCameraTypeText(config: CameraConfigState): string {
        return cameraTypeStr?.[config.cameraType] || cameraTypeStr?.[EdgeCamera.CameraType.StaticCamera];
    }

    // Open configure dialog for selected cameras
    public openConfigureDialog(): void {
        const selectedConfigs = this.cameraConfigsArray.filter(c => this.selectedCameraIds.has(c.id));

        if (selectedConfigs.length === 0) return;

        const dialogRef = this.dialog.open(ConfigureCamerasDialogComponent, {
            width: '760px',
            data: { cameras: selectedConfigs },
            panelClass: 'dialog-no-padding'
        });

        dialogRef.afterClosed().subscribe((updatedCameras: CameraConfigState[]) => {
            if (updatedCameras) {
                // Update configs using the returned objects
                updatedCameras.forEach(updated => {
                    this.cameraConfigs.set(updated.id, updated);
                });
            }
            this.clearSelection();
        });
    }

    // Emit finish event with all camera configs
    public onFinish(): void {
        const configs = this.cameraConfigsArray;
        this.finishSetup.emit(configs);
    }

    // Go back
    public onBack(): void {
        this.back.emit();
    }

    // Update camera name
    public onCameraNameChange(cameraId: string, newName: string): void {
        const config = this.cameraConfigs.get(cameraId);
        if (config) {
            config.name = newName;
        }
    }

    // Clear all selections
    public clearSelection(): void {
        this.selectedCameraIds.clear();
        this.selectedConfigsChanged.emit(this.selectedCameraIds);
    }

    // Camera info helpers
    public getCameraIp(config: CameraConfigState): string {
        return config.connectionDetails?.cameraDocument?.networkDetails?.ipAddress
            || config.originalCamera?.ipAddress
            || '—';
    }

    public getCameraMac(config: CameraConfigState): string {
        return config.connectionDetails?.cameraDocument?.networkDetails?.macAddress
            || config.originalCamera?.macAddress
            || config.originalCamera?.mac
            || '—';
    }

    public getCameraBrand(config: CameraConfigState): string {
        return config.originalCamera?.cameraBrand || '—';
    }

    public getConnectionStatusClass(config: CameraConfigState): string {
        const code = config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode;
        if (code >= 200 && code < 300) {
            return 'status-online';
        } else if (code === 401 || code === 404) {
            return 'status-warning';
        } else if (code) {
            return 'status-offline';
        }
        return 'status-unknown';
    }

    public getConnectionStatusText(config: CameraConfigState): string {
        const code = config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode;
        if (code >= 200 && code < 300) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.CONNECTION_STATUS.REASONABLE';
        } else if (code === 401) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.CONNECTION_STATUS.AUTH_ERROR';
        } else if (code === 404) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.CONNECTION_STATUS.NOT_FOUND';
        } else if (code) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.CONNECTION_STATUS.ERROR';
        }
        return 'ADD_CAMERA_WIZARD.CONFIGURATION.CONNECTION_STATUS.UNKNOWN';
    }

    public getCameraProtocol(config: CameraConfigState): string {
        const forceTcp = config.connectionDetails?.cameraDocument?.networkDetails?.forceTcp;
        return forceTcp ? 'ADD_CAMERA_WIZARD.CONFIGURATION.PROTOCOL.TCP' : 'ADD_CAMERA_WIZARD.CONFIGURATION.PROTOCOL.UDP';
    }

    // Stream optimization helpers
    private getStreamSettings(pixelCount: number, settings: StreamSetting[]): StreamSetting {
        let result: StreamSetting = settings[0];
        for (const entry of settings) {
            if (pixelCount < entry.pixelsCount) return result;
            result = entry;
        }
        return result;
    }

    // Check if mainstream is optimized (no warnings)
    public isMainstreamOptimized(config: CameraConfigState): boolean {
        const extended = config.connectionDetails?.mainStreamConnectionData?.extendedStreamData;
        if (!extended) return true; // No data, assume optimized
        return this.getMainstreamWarnings(config).length === 0;
    }

    // Check if substream is optimized (no warnings)
    public isSubstreamOptimized(config: CameraConfigState): boolean {
        const extended = config.connectionDetails?.subStreamConnectionData?.extendedStreamData;
        if (!extended) return true; // No data, assume optimized
        return this.getSubstreamWarnings(config).length === 0;
    }

    // Get mainstream warnings with recommended values
    public getMainstreamWarnings(config: CameraConfigState): { field: string; current: any; recommended: any }[] {
        const extended = config.connectionDetails?.mainStreamConnectionData?.extendedStreamData;
        if (!extended) return [];

        const pixels = (extended.width || 0) * (extended.height || 0);
        const streamSettings = this.getStreamSettings(pixels, SettingsMainstream);

        return this.getStreamWarnings(extended, streamSettings, 'mainstream', pixels);
    }

    // Get substream warnings with recommended values
    public getSubstreamWarnings(config: CameraConfigState): { field: string; current: any; recommended: any }[] {
        const extended = config.connectionDetails?.subStreamConnectionData?.extendedStreamData;
        if (!extended) return [];

        const pixels = (extended.width || 0) * (extended.height || 0);
        const streamSettings = this.getStreamSettings(pixels, SettingsSubstream);

        return this.getStreamWarnings(extended, streamSettings, 'substream', pixels);
    }

    private getStreamWarnings(
        extended: LocationModel.ExtendedStreamData,
        streamSettings: StreamSetting,
        streamType: 'mainstream' | 'substream',
        pixels: number
    ): { field: string; current: any; recommended: any }[] {
        const warnings: { field: string; current: any; recommended: any }[] = [];

        // Resolution check
        if (streamType === 'mainstream') {
            if (pixels < 1920 * 1080) {
                warnings.push({ field: 'resolution', current: `${extended.width}x${extended.height}`, recommended: '1920x1080+' });
            }
            if (extended.width > 4000) {
                warnings.push({ field: 'resolution', current: `${extended.width}x${extended.height}`, recommended: '<= 4K' });
            }
        } else {
            if (pixels > 1920 * 1080) {
                warnings.push({ field: 'resolution', current: `${extended.width}x${extended.height}`, recommended: '1920x1080 max' });
            }
        }

        // FPS check
        if (extended.averageFps && extended.averageFps < streamSettings.fps * 0.9) {
            warnings.push({ field: 'fps', current: extended.averageFps, recommended: streamSettings.fps });
        } else if (extended.averageFps && extended.averageFps > streamSettings.fps * 1.1) {
            warnings.push({ field: 'fps', current: extended.averageFps, recommended: streamSettings.fps });
        }

        // Min bitrate check
        if (extended.minBitrate !== undefined && extended.minBitrate < 10) {
            warnings.push({ field: 'minBitrate', current: extended.minBitrate, recommended: streamSettings.bitrateKbps });
        }

        // Max bitrate check
        if (extended.maxBitrate !== undefined && extended.maxBitrate > streamSettings.bitrateKbps * 1.2) {
            warnings.push({ field: 'maxBitrate', current: extended.maxBitrate, recommended: streamSettings.bitrateKbps });
        }

        // Keyframe interval check
        const recKeyframeInterval = streamSettings.iframeFactor * streamSettings.fps;
        if (extended.averageKeyFrameDistance !== undefined) {
            if (extended.averageKeyFrameDistance > recKeyframeInterval * 1.1) {
                warnings.push({ field: 'keyframeInterval', current: extended.averageKeyFrameDistance, recommended: recKeyframeInterval });
            } else if (extended.averageKeyFrameDistance < recKeyframeInterval * 0.9) {
                warnings.push({ field: 'keyframeInterval', current: extended.averageKeyFrameDistance, recommended: recKeyframeInterval });
            }
        }

        // Codec check (H265/HEVC recommended)
        if (extended.codecName && extended.codecName !== 'H265' && extended.codecName !== 'HEVC') {
            warnings.push({ field: 'codec', current: extended.codecName, recommended: 'H265' });
        }

        // Pixel format check
        if (extended.pixelFormat) {
            const pf = extended.pixelFormat.toLowerCase();
            if (pf !== 'yuv420p' && pf !== 'yuvj420p') {
                warnings.push({ field: 'pixelFormat', current: extended.pixelFormat, recommended: 'yuv420p' });
            }
        }

        // B-frames check
        if (extended.hasBframes) {
            warnings.push({
                field: 'bFrames',
                current: this.translateService.instant('COMMON.ENABLED'),
                recommended: this.translateService.instant('COMMON.DISABLED')
            });
        }

        return warnings;
    }

    // Build stream detail rows for the table
    public getStreamDetailRows(config: CameraConfigState, streamType: 'mainstream' | 'substream'): StreamDetailRow[] {
        const streamData = streamType === 'mainstream'
            ? config.connectionDetails?.mainStreamConnectionData
            : config.connectionDetails?.subStreamConnectionData;

        const extended = streamData?.extendedStreamData;
        if (!extended) return [];

        const warnings = streamType === 'mainstream'
            ? this.getMainstreamWarnings(config)
            : this.getSubstreamWarnings(config);

        const warningsMap = new Map(warnings.map(w => [w.field, w.recommended]));

        const rows: StreamDetailRow[] = [
            {
                label: 'CAMERA_ACTIVATE_DIALOG.TABLE.WIDTH',
                current: extended.width || '—',
                setTo: warningsMap.has('resolution') ? warningsMap.get('resolution') : undefined
            },
            {
                label: 'CAMERA_ACTIVATE_DIALOG.TABLE.HEIGHT',
                current: extended.height || '—'
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.MAX_BITRATE_KBPS',
                current: extended.maxBitrate !== undefined ? this.formatNumber(extended.maxBitrate) : '—',
                setTo: warningsMap.has('maxBitrate') ? this.formatNumber(warningsMap.get('maxBitrate')) : undefined
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.MIN_BITRATE_KBPS',
                current: extended.minBitrate !== undefined ? this.formatNumber(extended.minBitrate) : '—',
                setTo: warningsMap.has('minBitrate') ? this.formatNumber(warningsMap.get('minBitrate')) : undefined
            },
            {
                label: 'CAMERA_ACTIVATE_DIALOG.TABLE.FPS',
                current: extended.averageFps || '—',
                setTo: warningsMap.has('fps') ? warningsMap.get('fps') : undefined
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.VIDEO_CODEC',
                current: extended.codecName || '—',
                setTo: warningsMap.has('codec') ? warningsMap.get('codec') : undefined
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.AUDIO_CODEC',
                current: '—'  // Audio codec info not available in extendedStreamData
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.KEYFRAME_INTERVAL',
                current: extended.averageKeyFrameDistance || '—',
                setTo: warningsMap.has('keyframeInterval') ? warningsMap.get('keyframeInterval') : undefined
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.PIXEL_FORMAT',
                current: extended.pixelFormat || '—',
                setTo: warningsMap.has('pixelFormat') ? warningsMap.get('pixelFormat') : undefined
            },
            {
                label: 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_DETAILS.B_FRAMES',
                current: extended.hasBframes ? this.translateService.instant('COMMON.TRUE') : this.translateService.instant('COMMON.FALSE'),
                setTo: warningsMap.has('bFrames') ? warningsMap.get('bFrames') : undefined
            }
        ];

        return rows;
    }

    // Check if any row has a "set to" value (for showing the column)
    public hasSetToValues(config: CameraConfigState, streamType: 'mainstream' | 'substream'): boolean {
        const rows = this.getStreamDetailRows(config, streamType);
        return rows.some(row => row.setTo !== undefined);
    }

    private formatNumber(value: any): string {
        if (typeof value === 'number') {
            return value.toLocaleString();
        }
        return String(value);
    }

    // Get stream status icon class
    public getStreamOptimizationIcon(config: CameraConfigState, streamType: 'mainstream' | 'substream'): string {
        const code = streamType === 'mainstream'
            ? config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode
            : config.connectionDetails?.subStreamConnectionData?.connectionResponseCode;

        // If not connected, show appropriate icon
        if (!code || code < 200 || code >= 300) {
            if (!code) return 'ui-icon-minus status-none';
            if (code === 401 || code === 404) return 'ui-icon-warning status-warning';
            return 'ui-icon-error status-error';
        }

        // If connected, check optimization
        const isOptimized = streamType === 'mainstream'
            ? this.isMainstreamOptimized(config)
            : this.isSubstreamOptimized(config);

        return isOptimized ? 'ui-icon-status-online status-success' : 'ui-icon-warning status-warning';
    }

    // Get stream status tooltip
    public getStreamStatusTooltip(config: CameraConfigState, streamType: 'mainstream' | 'substream'): string {
        const code = streamType === 'mainstream'
            ? config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode
            : config.connectionDetails?.subStreamConnectionData?.connectionResponseCode;

        if (!code || code < 200 || code >= 300) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.NOT_CONNECTED_TOOLTIP';
        }

        const isOptimized = streamType === 'mainstream'
            ? this.isMainstreamOptimized(config)
            : this.isSubstreamOptimized(config);

        return isOptimized
            ? 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.OPTIMIZED_TOOLTIP'
            : 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.NOT_OPTIMIZED_TOOLTIP';
    }

    // Get stream menu title
    public getStreamMenuTitle(config: CameraConfigState, streamType: 'mainstream' | 'substream'): string {
        const code = streamType === 'mainstream'
            ? config.connectionDetails?.mainStreamConnectionData?.connectionResponseCode
            : config.connectionDetails?.subStreamConnectionData?.connectionResponseCode;

        if (!code || code < 200 || code >= 300) {
            return 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.NOT_CONNECTED';
        }

        const isOptimized = streamType === 'mainstream'
            ? this.isMainstreamOptimized(config)
            : this.isSubstreamOptimized(config);

        return isOptimized
            ? 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.OPTIMIZED'
            : 'ADD_CAMERA_WIZARD.CONFIGURATION.STREAM_STATUS.NOT_OPTIMIZED';
    }
}
